import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { AuthContext } from "./useAuth";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  // Track the last user id we've applied so duplicate auth events with the
  // same user (e.g. SIGNED_IN → INITIAL_SESSION → getSession on hard reload)
  // don't churn the session object reference and re-trigger downstream
  // effects that key on `user` (causing a double/triple-render flicker).
  const lastUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const applySession = (s: Session | null) => {
      const nextId = s?.user?.id ?? null;
      if (lastUserIdRef.current === nextId) {
        // Same user (or still signed out) — keep the existing session object
        // reference so consumers of `user` don't see a new identity.
        setLoading(false);
        return;
      }
      lastUserIdRef.current = nextId;
      setSession(s);
      setLoading(false);
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      applySession(s);
    });
    supabase.auth.getSession().then(({ data }) => {
      applySession(data.session);
    });
    return () => sub.subscription.unsubscribe();
  }, []);


  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) console.error("[auth] signOut error", error);
    } catch (err) {
      console.error("[auth] signOut threw", err);
    }
    // Belt-and-braces: nuke any leftover supabase auth tokens from localStorage
    // so a transient signOut() failure can't leave the user silently signed in.
    if (typeof window !== "undefined") {
      try {
        for (let i = window.localStorage.length - 1; i >= 0; i--) {
          const key = window.localStorage.key(i);
          if (key && key.startsWith("sb-") && key.endsWith("-auth-token")) {
            window.localStorage.removeItem(key);
          }
        }
      } catch {
        // localStorage can throw in private mode — ignore.
      }
      // Hard navigate so all in-memory state (TanStack Query caches, workspace
      // context, etc.) is flushed and the user lands cleanly on the marketing
      // home page rather than bouncing through /auth via ProtectedRoute.
      window.location.assign("/");
    }
  };

  return (
    <AuthContext.Provider value={{ user: session?.user ?? null, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
